import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getProducts, getProductsAlreadyOrdered } from 'interfaces/products';
import { MdArrowBack, MdCheckCircle, MdAdd, MdRemove, MdFavorite } from 'react-icons/md';
import { getTotalHT, getTotalTTC, isCustomerHT } from 'views/orders/createOrders/utils';

interface ProductSelectionProps {
    customer: any;
    initialSelectedProducts?: any[];
    onBack: () => void;
    onConfirm: (products: any[]) => void;
    isProcessing: boolean;
}

const ProductSelection = ({ customer, initialSelectedProducts, onBack, onConfirm, isProcessing }: ProductSelectionProps) => {
    const [products, setProducts] = useState<any[]>([]);
    const [filteredProducts, setFilteredProducts] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProducts, setSelectedProducts] = useState<any[]>(initialSelectedProducts || []);
    const [isLoading, setIsLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [showAvailableOnMobile, setShowAvailableOnMobile] = useState(false);
    const [alreadyOrderedIds, setAlreadyOrderedIds] = useState<string[]>([]);
    const [visibleCount, setVisibleCount] = useState(10);

    useEffect(() => {
        initProducts();
    }, []);

    useEffect(() => {
        const updateMobile = () => setIsMobile(window.innerWidth < 1024);
        updateMobile();
        window.addEventListener('resize', updateMobile);
        return () => window.removeEventListener('resize', updateMobile);
    }, []);

    useEffect(() => {
        filterProducts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, products, isMobile, showAvailableOnMobile, alreadyOrderedIds]);

    useEffect(() => {
        setVisibleCount(10);
    }, [searchTerm, showAvailableOnMobile]);

    const initProducts = async () => {
        setIsLoading(true);
        try {
            const [res, alreadyOrderedRes] = await Promise.all([
                getProducts(1, 10000, customer?._id || null),
                customer?._id ? getProductsAlreadyOrdered(customer._id) : Promise.resolve(null),
            ]);

            if (alreadyOrderedRes && Array.isArray(alreadyOrderedRes)) {
                setAlreadyOrderedIds(alreadyOrderedRes.map((p: any) => p._id));
            }

            if (res && res.data) {
                const orderedIds = (alreadyOrderedRes && Array.isArray(alreadyOrderedRes))
                    ? alreadyOrderedRes.map((p: any) => p._id)
                    : [];

                const recent = res.data.filter((p: any) => orderedIds.includes(p._id));
                const others = res.data.filter((p: any) => !orderedIds.includes(p._id));
                const sorted = [...recent, ...others];

                setProducts(sorted);
                setFilteredProducts(sorted);
            }
        } catch (error) {
            console.error('Error loading products:', error);
            toast.error('Erreur lors du chargement des produits');
        } finally {
            setIsLoading(false);
        }
    };

    const filterProducts = () => {
        if (isMobile && !showAvailableOnMobile) {
            setFilteredProducts([]);
            return;
        }

        if (!searchTerm) {
            const recent = products.filter((p: any) => alreadyOrderedIds.includes(p._id));
            const others = products.filter((p: any) => !alreadyOrderedIds.includes(p._id));
            setFilteredProducts([...recent, ...others]);
            return;
        }

        const filtered = products.filter((product: any) => {
            const searchLower = searchTerm.toLowerCase();
            return (
                product.name?.toLowerCase().includes(searchLower) ||
                product.ref?.toLowerCase().includes(searchLower) ||
                product.ean?.toLowerCase().includes(searchLower)
            );
        });
        const recent = filtered.filter((p: any) => alreadyOrderedIds.includes(p._id));
        const others = filtered.filter((p: any) => !alreadyOrderedIds.includes(p._id));
        setFilteredProducts([...recent, ...others]);
    };

    const addProduct = (product: any) => {
        const existingProduct = selectedProducts.find(p => p._id === product._id);
        if (existingProduct) {
            updateQuantity(product._id, existingProduct.quantity + 1);
        } else {
            setSelectedProducts([
                ...selectedProducts,
                {
                    ...product,
                    productId: product._id,
                    productName: product.name,
                    quantity: 1,
                    price: parseFloat(product.price) || 0,
                    discountType: 'none',
                    discountValue: 0,
                    comment: '',
                }
            ]);
        }
    };

    const removeProduct = (productId: string) => {
        setSelectedProducts(selectedProducts.filter(p => p._id !== productId));
    };

    const updateQuantity = (productId: string, quantity: number) => {
        if (quantity <= 0) {
            removeProduct(productId);
            return;
        }
        setSelectedProducts(
            selectedProducts.map(p =>
                p._id === productId ? { ...p, quantity } : p
            )
        );
    };

    const updatePrice = (productId: string, price: number) => {
        setSelectedProducts(
            selectedProducts.map(p =>
                p._id === productId ? { ...p, price } : p
            )
        );
    };

    const updateLotNumber = (productId: string, lotNumber: string) => {
        setSelectedProducts(
            selectedProducts.map(p =>
                p._id === productId ? { ...p, lotNumber } : p
            )
        );
    };

    const updateDiscountType = (productId: string, discountType: string) => {
        setSelectedProducts(
            selectedProducts.map(p =>
                p._id === productId
                    ? { ...p, discountType, discountValue: discountType === 'none' ? 0 : p.discountValue }
                    : p
            )
        );
    };

    const updateDiscountValue = (productId: string, discountValue: number) => {
        setSelectedProducts(
            selectedProducts.map(p =>
                p._id === productId ? { ...p, discountValue } : p
            )
        );
    };

    const getUniqueLots = (product: any): string[] => {
        const availableLots =
            product?.inventory
                ?.filter((inv: any) => inv?.lotNumber && inv?.quantity > 0)
                .map((inv: any) => inv.lotNumber as string) || [];
        return Array.from(new Set(availableLots));
    };

    const handleConfirm = () => {
        if (selectedProducts.length === 0) {
            toast.warning('Veuillez ajouter au moins un produit');
            return;
        }
        onConfirm(selectedProducts);
    };

    const totalHT = getTotalHT(selectedProducts);
    const customerIsHT = isCustomerHT(customer);
    const totalTTC = getTotalTTC(selectedProducts, { isCustomerHT: customerIsHT });
    const displayedProducts = filteredProducts.slice(0, visibleCount);
    const canShowMore = filteredProducts.length > visibleCount;

    return (
        <div className="flex flex-col gap-4">
            {/* Search bar */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <button
                    onClick={onBack}
                    disabled={isProcessing}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 rounded-xl border-2 border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition duration-200 hover:bg-gray-50 dark:border-gray-600 dark:text-white dark:hover:bg-gray-800"
                >
                    <MdArrowBack className="h-5 w-5" />
                    Retour
                </button>
                <div className="flex-1">
                    <input
                        type="text"
                        placeholder="Rechercher un produit par nom, référence ou EAN..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm outline-none transition-all focus:border-toola-500 dark:border-gray-600 dark:bg-navy-800 dark:text-white"
                    />
                </div>
                {isMobile && (
                    <button
                        onClick={() => setShowAvailableOnMobile((prev) => !prev)}
                        disabled={isProcessing}
                        className="lg:hidden w-full sm:w-auto rounded-xl bg-lightPrimary px-4 py-2 text-sm font-medium text-navy-700 dark:bg-navy-800 dark:text-white"
                    >
                        {showAvailableOnMobile ? 'Masquer les produits' : 'Afficher les produits'}
                    </button>
                )}
            </div>

            {isMobile && showAvailableOnMobile && (
                <p className="text-xs text-gray-500 px-1">
                    Les produits déjà commandés apparaissent en premier.
                </p>
            )}

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {/* Available products */}
                <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                    <h3 className="mb-3 font-bold text-navy-700 dark:text-white">
                        Produits disponibles ({filteredProducts.length})
                    </h3>
                    <div className="max-h-[300px] lg:max-h-[400px] space-y-2 overflow-y-auto pr-2">
                        {isLoading ? (
                            <p className="text-center text-sm text-gray-500">Chargement...</p>
                        ) : filteredProducts.length === 0 ? (
                            <p className="text-center text-sm text-gray-500">Aucun produit trouvé</p>
                        ) : (
                            displayedProducts.map((product: any) => (
                                <div
                                    key={product._id}
                                    className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3 transition-all hover:border-toola-500 hover:shadow-md dark:border-gray-700 dark:bg-navy-800"
                                >
                                    <div className="flex-1">
                                        <p className="font-semibold text-navy-700 dark:text-white flex items-center gap-1">
                                            {product.name}
                                            {alreadyOrderedIds.includes(product._id) && (
                                                <MdFavorite
                                                    className="h-4 w-4 text-red-500"
                                                    title="Déjà commandé"
                                                />
                                            )}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            Réf: {product.ref} | {product.price}€
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => addProduct(product)}
                                        disabled={isProcessing}
                                        className="ml-3 flex h-8 w-8 items-center justify-center rounded-full bg-toola-500 text-white transition duration-200 hover:bg-toola-600 active:bg-toola-700"
                                    >
                                        <MdAdd className="h-5 w-5" />
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                    {canShowMore && (
                        <button
                            onClick={() => setVisibleCount((prev) => prev + 10)}
                            disabled={isProcessing}
                            className="mt-3 w-full rounded-xl border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition duration-200 hover:bg-gray-100 dark:border-gray-600 dark:text-white dark:hover:bg-gray-800"
                        >
                            Voir plus ({filteredProducts.length - visibleCount} restants)
                        </button>
                    )}
                </div>

                {/* Selected products */}
                <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                    <h3 className="mb-3 font-bold text-navy-700 dark:text-white">
                        Produits sélectionnés ({selectedProducts.length})
                    </h3>
                    <div className="max-h-[350px] lg:max-h-[400px] space-y-2 overflow-y-auto pr-2">
                        {selectedProducts.length === 0 ? (
                            <p className="text-center text-sm text-gray-500">
                                Aucun produit sélectionné
                            </p>
                        ) : (
                            selectedProducts.map((product: any) => (
                                (() => {
                                    const uniqueLots = getUniqueLots(product);
                                    const datalistId = `lotNumbers-${product._id}`;
                                    return (
                                <div
                                    key={product._id}
                                    className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-navy-800"
                                >
                                    <div className="mb-2 flex items-center justify-between">
                                        <p className="font-semibold text-navy-700 dark:text-white flex items-center gap-1">
                                            {product.name}
                                            {alreadyOrderedIds.includes(product._id) && (
                                                <MdFavorite
                                                    className="h-4 w-4 text-red-500"
                                                    title="Déjà commandé"
                                                />
                                            )}
                                        </p>
                                        <button
                                            onClick={() => removeProduct(product._id)}
                                            disabled={isProcessing}
                                            className="text-red-500 transition duration-200 hover:text-red-700"
                                        >
                                            <MdRemove className="h-5 w-5" />
                                        </button>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <div className="flex items-center rounded-lg border border-gray-300 dark:border-gray-600">
                                            <button
                                                onClick={() => updateQuantity(product._id, product.quantity - 1)}
                                                disabled={isProcessing}
                                                className="px-2 py-1 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                                            >
                                                -
                                            </button>
                                            <input
                                                type="number"
                                                value={product.quantity}
                                                onChange={(e) => updateQuantity(product._id, parseInt(e.target.value) || 0)}
                                                disabled={isProcessing}
                                                className="w-16 border-x border-gray-300 bg-transparent px-2 py-1 text-center text-sm outline-none dark:border-gray-600"
                                            />
                                            <button
                                                onClick={() => updateQuantity(product._id, product.quantity + 1)}
                                                disabled={isProcessing}
                                                className="px-2 py-1 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                                            >
                                                +
                                            </button>
                                        </div>
                                        <input
                                            type="number"
                                            step="0.1"
                                            value={product.price}
                                            onChange={(e) => updatePrice(product._id, parseFloat(e.target.value) || 0)}
                                            disabled={isProcessing}
                                            className="w-24 rounded-lg border border-gray-300 px-2 py-1 text-sm outline-none dark:border-gray-600 dark:bg-navy-900"
                                            placeholder="Prix"
                                        />
                                        <span className="text-sm text-gray-500">€</span>
                                        <span className="ml-auto text-sm font-semibold text-navy-700 dark:text-white">
                                            {(product.quantity * product.price).toFixed(2)}€
                                        </span>
                                    </div>
                                    <div className="mt-2">
                                        <input
                                            type="text"
                                            value={product.lotNumber || ''}
                                            onChange={(e) => updateLotNumber(product._id, e.target.value)}
                                            disabled={isProcessing}
                                            list={uniqueLots.length > 0 ? datalistId : undefined}
                                            className="w-full rounded-lg border border-gray-300 px-2 py-1 text-sm outline-none dark:border-gray-600 dark:bg-navy-900"
                                            placeholder="Lot (optionnel)"
                                        />
                                        {uniqueLots.length > 0 && (
                                            <datalist id={datalistId}>
                                                {uniqueLots.map((lot, idx) => (
                                                    <option key={`${lot}-${idx}`} value={lot}>
                                                        {lot}
                                                    </option>
                                                ))}
                                            </datalist>
                                        )}
                                    </div>
                                    <div className="mt-2 flex items-center gap-2">
                                        <select
                                            value={product.discountType || 'none'}
                                            onChange={(e) => updateDiscountType(product._id, e.target.value)}
                                            disabled={isProcessing}
                                            className="rounded-lg border border-gray-300 px-2 py-1 text-sm outline-none dark:border-gray-600 dark:bg-navy-900"
                                        >
                                            <option value="none">Remise</option>
                                            <option value="percent">%</option>
                                            <option value="value">€</option>
                                        </select>
                                        {product.discountType && product.discountType !== 'none' && (
                                            <>
                                                <input
                                                    type="number"
                                                    step="1"
                                                    min="0"
                                                    value={product.discountValue || ''}
                                                    onChange={(e) => updateDiscountValue(product._id, parseFloat(e.target.value) || 0)}
                                                    disabled={isProcessing}
                                                    className="w-20 rounded-lg border border-gray-300 px-2 py-1 text-sm outline-none dark:border-gray-600 dark:bg-navy-900"
                                                    placeholder="0"
                                                />
                                                <span className="text-sm text-gray-500">
                                                    {product.discountType === 'percent' ? '%' : '€'}
                                                </span>
                                                <span className="ml-auto text-xs text-green-600 font-medium">
                                                    -{product.discountType === 'percent'
                                                        ? ((product.price * product.quantity) * ((product.discountValue || 0) / 100)).toFixed(2)
                                                        : (product.discountValue || 0).toFixed(2)}€
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                    );
                                })()
                            ))
                        )}
                    </div>

                    {selectedProducts.length > 0 && (
                        <div className="mt-4 rounded-lg bg-gray-50 p-3 dark:bg-navy-900">
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">Total HT:</span>
                                <span className="font-bold text-navy-700 dark:text-white">
                                    {totalHT.toFixed(2)}€
                                </span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">Total TTC:</span>
                                <span className="font-bold text-navy-700 dark:text-white">
                                    {totalTTC.toFixed(2)}€ {isCustomerHT ? '(client HT)' : ''}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-4 flex justify-end sticky bottom-0 bg-white/90 dark:bg-navy-900/90 backdrop-blur py-2">
                <button
                    onClick={handleConfirm}
                    disabled={selectedProducts.length === 0 || isProcessing}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 rounded-xl bg-toola-500 px-6 py-3 text-base font-medium text-white transition duration-200 hover:bg-toola-600 active:bg-toola-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {isProcessing ? (
                        <>
                            <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                            Création en cours...
                        </>
                    ) : (
                        <>
                            <MdCheckCircle className="h-5 w-5" />
                            Créer la commande
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default ProductSelection;
